import { useMemo } from 'react'
import ErrorBoundary from '@/components/ErrorBoundary'
import { useConfirmation } from '@/contexts/confirmation'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import DownIcons from '@/icons/DownIcons'
import LoadIcons from '@/icons/LoadIcons'
import { Contract, ServiceConfigOption } from '@/modules/Contract/types'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AlertError, Button, IconButton, Typogaphy } from 'vnetwork-ui'
import Base from './Base'
import ProductSummary from './Shared/ProductSummary'
import { getTemplateHints } from '@/components/QuickHelp'
import Template from './template'

interface ProductTemplateProps {
    index: number
    remove: (index: number) => void
    duplicate?: (index: number) => void
    isPOC?: boolean
    isAddon?: boolean
    fixedProductGroup?: string
    className?: string
}

const ProductTemplate: React.FC<ProductTemplateProps> = ({
    index,
    remove,
    duplicate,
    isPOC = false,
    isAddon = false,
    fixedProductGroup,
    className,
}) => {
    const { t } = useTranslation()
    const { setConfirmation } = useConfirmation()

    const { refetchCurrent } = useDataGridRefetch({
        reactQueryKey: `order.products.[${index}].product_group_name`,
    })

    const {
        control,
        setValue,
        formState: { errors },
    } = useFormContext<Contract>()
    const service = useWatch({
        control,
        name: `order.products.[${index}]` as Path<Contract>,
    })

    const product_group_name = useWatch({
        control,
        name: `order.products.[${index}].product_group_name` as Path<Contract>,
    }) as string

    const service_configoptions = useWatch({
        control,
        name: `order.products.[${index}].service_configoptions` as Path<Contract>,
    }) as ServiceConfigOption[]

    const editable = useWatch({
        control,
        name: `order.products.[${index}].editable` as Path<Contract>,
    })

    const service_price_register = useWatch({
        control,
        name: `order.products.[${index}].service_price_register` as Path<Contract>,
    })

    const template_name = useWatch({
        control,
        name: `order.products.[${index}].template_name` as Path<Contract>,
    }) as string | undefined

    // Convert template hints to QuickHelpOption format for floating menu
    const quickHelpOptions = useMemo<QuickHelpOption[]>(() => {
        if (!template_name) return []
        const hints = getTemplateHints(template_name)
        return hints.map((hint) => ({
            id: hint.id,
            label: hint.title,
            description: hint.description,
            selector: hint.tourSelector,
            tourTitle: hint.title,
            tourDescription: hint.description,
        }))
    }, [template_name])

    // Register hints with floating QuickHelp menu
    useQuickHelp(quickHelpOptions)

    const seteEditable = (value: boolean) => {
        setValue(`order.products.[${index}].editable` as Path<Contract>, value)
    }

    const handleToggleEditable = async () => {
        seteEditable(!editable) // Thu gọn về không editable
    }

    return (
        <ErrorBoundary>
            <div
                className={`border border-gray-100 rounded-[8px] p-4 ${editable ? '' : 'hidden'} ${className}`}
            >
                <div className="flex items-center gap-2">
                    <Typogaphy size="medium" className="font-medium">
                        {t('select-service')}
                    </Typogaphy>

                    <IconButton onClick={refetchCurrent} size="small">
                        <LoadIcons className="text-bg-primary" />
                    </IconButton>
                </div>

                <Base
                    index={index}
                    isPOC={isPOC}
                    isAddon={isAddon}
                    fixedProductGroup={fixedProductGroup}
                >
                    {(product, price) => {
                        const templateName = product.template_name

                        if (!templateName)
                            return (
                                <AlertError
                                    error={
                                        new Error(t('missing-template-name'))
                                    }
                                />
                            )

                        const ConfigComponent =
                            Template[templateName as keyof typeof Template]

                        if (!ConfigComponent)
                            return (
                                <AlertError
                                    error={new Error(t(templateName))}
                                />
                            )

                        return (
                            <ConfigComponent
                                index={index}
                                isPOC={isPOC}
                                product={product}
                                price={price}
                            />
                        )
                    }}
                </Base>
                <div className="flex justify-end items-center gap-4 mt-4 pt-4 border-t border-gray-100">
                    <Button
                        variant="outlined"
                        type="button"
                        color="error"
                        className="w-full md:w-[200px] border-red-500"
                        startIcon={<DeleteOutlineIcons />}
                        onClick={() =>
                            setConfirmation({
                                title: t('delete-product'),
                                description: t('delete-product-description'),
                                onConfirm: () => remove(index),
                                open: true,
                            })
                        }
                    >
                        {t('delete')}
                    </Button>
                    <Button
                        onClick={handleToggleEditable}
                        type="button"
                        color="primary"
                        variant="contained"
                        className="w-full md:w-[200px]"
                        startIcon={<DownIcons className="w-4 h-4" />}
                    >
                        {t('compact')}
                    </Button>
                </div>
            </div>

            <div className={`${editable ? 'hidden' : ''}`}>
                <ProductSummary
                    isPOC={isPOC}
                    service={service}
                    product_group_name={product_group_name}
                    service_configoptions={service_configoptions}
                    service_price_register={service_price_register}
                    hasErrors={
                        Object.keys(errors?.order?.products?.[index] || {})
                            ?.length > 0
                    }
                    onEdit={handleToggleEditable}
                    onDuplicate={() => duplicate?.(index)}
                    onDelete={() =>
                        setConfirmation({
                            title: t('delete-product'),
                            description: t('delete-product-description'),
                            onConfirm: () => remove(index),
                            open: true,
                        })
                    }
                />
            </div>
        </ErrorBoundary>
    )
}

export default ProductTemplate
